import { faCircleInfo } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/videa-components/src/index';

import { BenchmarkChip } from '../components/BenchmarkChip';
import {
  calculateBenchmarks,
  calculateDistribution,
} from '../helpers/benchmarking/calculateBenchmarks';
import { useOverallRates } from '../helpers/benchmarking/overallRates';
import { useProviderRates } from '../helpers/benchmarking/providerRates';

import { BenchmarkingDistributionChart } from './BenchmarkingDistributionChart';

export const ConversionRateChart = ({
  level,
  practices,
  providers,
}: {
  level?: 'Provider' | 'Practice' | 'Region' | 'DSO';
  practices?: string[];
  providers?: string[];
}) => {
  const { providerRates: providerData, isLoading } = useProviderRates({});

  const overallRates = useOverallRates({
    practices: practices,
    providers: providers,
  });

  const plannedRatesBenchmarks = calculateBenchmarks(
    providerData
      ?.map((provider) => provider?.plannedRate)
      ?.filter((rate) => rate !== undefined),
  );
  const acceptanceRatesBenchmarks = calculateBenchmarks(
    providerData
      ?.map((provider) => provider?.acceptanceRate)
      ?.filter((rate) => rate !== undefined),
  );
  const completionRatesBenchmarks = calculateBenchmarks(
    providerData
      ?.map((provider) => provider?.completionRate)
      ?.filter((rate) => rate !== undefined),
  );
  const conversionRatesBenchmarks = calculateBenchmarks(
    providerData
      ?.map((provider) => provider?.conversionRate)
      ?.filter((rate) => rate !== undefined),
  );

  const plannedRateDistribution = calculateDistribution(
    providerData
      ?.map((provider) => provider?.plannedRate)
      ?.filter((rate) => rate !== undefined),
  );
  const acceptanceRateDistribution = calculateDistribution(
    providerData
      ?.map((provider) => provider?.acceptanceRate)
      ?.filter((rate) => rate !== undefined),
  );
  const completionRateDistribution = calculateDistribution(
    providerData
      ?.map((provider) => provider?.completionRate)
      ?.filter((rate) => rate !== undefined),
  );
  const conversionRateDistribution = calculateDistribution(
    providerData
      ?.map((provider) => provider?.conversionRate)
      ?.filter((rate) => rate !== undefined),
    20,
    0.5,
  );

  return (
    <div className="vai-bg-white vai-rounded-lg vai-px-4 vai-border-gray-200 vai-border-[1px] vai-w-[66%] vai-h-full">
      <div className="vai-flex vai-flex-col vai-h-1/2 vai-border-b-[1px] vai-border-gray-200 vai-py-4">
        <div className="vai-flex vai-flex-row vai-justify-between vai-w-full">
          <span className="vai-typography-small vai-font-semibold vai-flex vai-flex-row vai-gap-2 vai-items-center">
            Conversion Rate
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger>
                  <FontAwesomeIcon
                    icon={faCircleInfo}
                    className="vai-h-4 vai-w-4 vai-text-zinc-400"
                  />
                </TooltipTrigger>
                <TooltipContent className="vai-bg-zinc-800 vai-opacity-90">
                  <div className="vai-flex vai-flex-col vai-gap-2 vai-w-[300px] vai-text-white vai-typography-small">
                    <span className="vai-font-light">
                      <span className="vai-font-bold">Definition:</span> The
                      portion of total AI recommendations that went on to be
                      completed.
                    </span>
                    <span className="vai-font-light">
                      <span className="vai-font-bold">Formula:</span>
                      Completed ÷ Total AI Recommendations
                    </span>
                    <span className="vai-font-light">
                      <span className="vai-font-bold">Example:</span> If the AI
                      surfaced 100 opportunities and 32 of those were completed,
                      your conversion rate is 32%.
                    </span>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </span>
          {level !== 'DSO' && <BenchmarkChip
            value={overallRates?.conversionRate}
            benchmark={{
              lowerBenchmark: conversionRatesBenchmarks?.lowerBenchmark,
              upperBenchmark: conversionRatesBenchmarks?.upperBenchmark,
            }}
          />}
        </div>
        <div className="vai-flex vai-flex-row vai-items-center vai-gap-4 vai-typography-muted">
          <div className="vai-flex vai-flex-row vai-items-center vai-gap-1">
            <span className="vai-bg-[#E4E4E7] vai-w-3 vai-h-3 vai-rounded-[2px]" />
            <span>Count of Providers (DSO)</span>
          </div>
          <div className="vai-flex vai-flex-row vai-items-center vai-gap-1">
            <span className="vai-w-4 vai-border-b-[2px] vai-border-black vai-border-dashed" />
            <span>{level} Average</span>
          </div>
        </div>
        <BenchmarkingDistributionChart
          rate={overallRates?.conversionRate}
          previousRate={overallRates?.previousConversionRate}
          lowerBenchmark={conversionRatesBenchmarks?.lowerBenchmark}
          upperBenchmark={conversionRatesBenchmarks?.upperBenchmark}
          distribution={conversionRateDistribution}
          isLoading={isLoading}
          isConversionRate={true}
        />
      </div>
      <div className="vai-flex vai-h-1/2 vai-flex-row vai-pt-6 vai-gap-4">
        <div className="vai-flex vai-flex-col vai-w-[32%] vai-h-full vai-border-r-[1px] vai-pr-4 vai-border-gray-200 vai-gap-1">
          <div className="vai-flex vai-flex-row vai-justify-between vai-gap-2 vai-w-full">
            <span className="vai-typography-small vai-font-semibold vai-flex vai-flex-row vai-gap-2 vai-items-center">
              Planned Rate
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger>
                    <FontAwesomeIcon
                      icon={faCircleInfo}
                      className="vai-h-4 vai-w-4 vai-text-zinc-400"
                    />
                  </TooltipTrigger>
                  <TooltipContent className="vai-bg-zinc-800 vai-opacity-90">
                    <div className="vai-flex vai-flex-col vai-gap-2 vai-w-[300px] vai-text-white vai-typography-small">
                      <span className="vai-font-light">
                        <span className="vai-font-bold">Definition:</span> The
                        percent of total AI recommendations that were treatment
                        planned by a provider.
                      </span>
                      <span className="vai-font-light">
                        <span className="vai-font-bold">Formula:</span>{' '}
                        Treatment Planned ÷ Total AI Recommendations
                      </span>
                      <span className="vai-font-light">
                        <span className="vai-font-bold">Example:</span> If the
                        AI surfaced 100 opportunities and the provider planned
                        60 of those recommendations, your planned rate is 60%.
                      </span>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </span>
            {level !== 'DSO' && <BenchmarkChip
              value={overallRates?.plannedRate}
              benchmark={{
                lowerBenchmark: plannedRatesBenchmarks?.lowerBenchmark,
                upperBenchmark: plannedRatesBenchmarks?.upperBenchmark,
              }}
            />}
          </div>
          <BenchmarkingDistributionChart
            rate={overallRates?.plannedRate}
            previousRate={overallRates?.previousPlannedRate}
            lowerBenchmark={plannedRatesBenchmarks?.lowerBenchmark}
            upperBenchmark={plannedRatesBenchmarks?.upperBenchmark}
            distribution={plannedRateDistribution}
            isLoading={isLoading}
          />
        </div>
        <div className="vai-flex vai-flex-col vai-h-full vai-w-[32%] vai-border-r-[1px] vai-pr-4 vai-border-gray-200 vai-gap-1">
          <div className="vai-flex vai-flex-row vai-justify-between vai-gap-2 vai-w-full">
            <span className="vai-typography-small vai-font-semibold vai-flex vai-flex-row vai-gap-2 vai-items-center">
              Scheduled Rate
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger>
                    <FontAwesomeIcon
                      icon={faCircleInfo}
                      className="vai-h-4 vai-w-4 vai-text-zinc-400"
                    />
                  </TooltipTrigger>
                  <TooltipContent className="vai-bg-zinc-800 vai-opacity-90">
                    <div className="vai-flex vai-flex-col vai-gap-2 vai-w-[300px] vai-text-white vai-typography-small">
                      <span className="vai-font-light">
                        <span className="vai-font-bold">Definition:</span> The
                        portion of planned AI opportunities that were scheduled.
                      </span>
                      <span className="vai-font-light">
                        <span className="vai-font-bold">Formula:</span>{' '}
                        Scheduled ÷ Planned AI Recommendations
                      </span>
                      <span className="vai-font-light">
                        <span className="vai-font-bold">Example:</span> If you
                        planned 60 AI opportunities and 40 of those were
                        scheduled, your scheduled rate is 40/60=67%.
                      </span>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </span>
            {level !== 'DSO' && <BenchmarkChip
              value={overallRates?.acceptanceRate}
              benchmark={{
                lowerBenchmark: acceptanceRatesBenchmarks?.lowerBenchmark,
                upperBenchmark: acceptanceRatesBenchmarks?.upperBenchmark,
              }}
            />}
          </div>
          <BenchmarkingDistributionChart
            rate={overallRates?.acceptanceRate}
            previousRate={overallRates?.previousAcceptanceRate}
            lowerBenchmark={acceptanceRatesBenchmarks?.lowerBenchmark}
            upperBenchmark={acceptanceRatesBenchmarks?.upperBenchmark}
            distribution={acceptanceRateDistribution}
            isLoading={isLoading}
          />
        </div>
        <div className="vai-flex vai-flex-col vai-h-full vai-w-[32%] vai-gap-1">
          <div className="vai-flex vai-flex-row vai-justify-between vai-gap-2 vai-w-full">
            <span className="vai-typography-small vai-font-semibold vai-flex vai-flex-row vai-gap-2 vai-items-center">
              Completed Rate
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger>
                    <FontAwesomeIcon
                      icon={faCircleInfo}
                      className="vai-h-4 vai-w-4 vai-text-zinc-400"
                    />
                  </TooltipTrigger>
                  <TooltipContent className="vai-bg-zinc-800 vai-opacity-90">
                    <div className="vai-flex vai-flex-col vai-gap-2 vai-w-[300px] vai-text-white vai-typography-small">
                      <span className="vai-font-light">
                        <span className="vai-font-bold">Definition:</span> The
                        portion of scheduled AI opportunities that were
                        completed.
                      </span>
                      <span className="vai-font-light">
                        <span className="vai-font-bold">Formula:</span>{' '}
                        Completed ÷ Scheduled AI Recommendations
                      </span>
                      <span className="vai-font-light">
                        <span className="vai-font-bold">Example:</span> If you
                        scheduled 40 AI opportunities and 32 of those were
                        completed, your completion rate is 32/40=80%.
                      </span>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </span>
            {level !== 'DSO' && <BenchmarkChip
              value={overallRates?.completionRate}
              benchmark={{
                lowerBenchmark: completionRatesBenchmarks?.lowerBenchmark,
                upperBenchmark: completionRatesBenchmarks?.upperBenchmark,
              }}
            />}
          </div>
          <BenchmarkingDistributionChart
            rate={overallRates?.completionRate}
            previousRate={overallRates?.previousCompletionRate}
            lowerBenchmark={completionRatesBenchmarks?.lowerBenchmark}
            upperBenchmark={completionRatesBenchmarks?.upperBenchmark}
            distribution={completionRateDistribution}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );
};
